import { OFFLINE_RETRY_ATTEMPTS_MAX, OFFLINE_RETRY_DELAY, RETRY_ATTEMPTS_MAX, RETRY_DELAY_MAX } from '../../constants';
import { API_CUSTOM_ERROR_CODES, HTTP_ERROR_CODES } from '../../errors';
import type { ApiError } from '../../fetch/ApiError';
import { getUIDHeaderValue, withUIDHeaders } from '../../fetch/headers';
import { wait } from '../../helpers/promise';
import type { HumanVerificationMethodType } from '../../interfaces/HumanVerification';
import { setRefreshCookies } from '../auth';
import { getApiError, getApiErrorMessage } from './apiErrorHelper';
import { AppVersionBadError, InactiveSessionError } from './errors';
import { getHumanVerificationData, withVerification } from './humanVerification';
import { createRefreshHandlers, getIsRefreshFailure, refresh } from './refreshHandlers';
import { retryHandler } from './retryHandler';

/**
 * Attach a catch handler to every API call to handle 401, 403, and other errors.
 */
export function withApiHandlers({
    call,
    onMissingScopes,
    onVerification,
    onUserRestricted,
}: {
    call: any;
    onMissingScopes: (data: any) => Promise<any>;
    onVerification: (data: any) => Promise<any>;
    onUserRestricted: (data: any) => Promise<any>;
}) {
    let loggedOut = false;
    let appVersionBad = false;

    const refreshHandler = createRefreshHandlers((UID) => {
        return refresh(() => call(withUIDHeaders(UID, setRefreshCookies())), 1, RETRY_ATTEMPTS_MAX);
    });

    let UID: string | undefined;

    const cb = (options: any) => {
        const perform = (attempts: number, maxAttempts?: number) => {
            if (loggedOut) {
                return Promise.reject(InactiveSessionError());
            }
            if (appVersionBad) {
                return Promise.reject(AppVersionBadError());
            }

            return call(options).catch((e: ApiError) => {
                if (loggedOut) {
                    throw InactiveSessionError();
                }

                if (maxAttempts && attempts >= maxAttempts) {
                    throw e;
                }

                const { status, name } = e as ApiError;

                const {
                    ignoreHandler,
                    headers,
                    retriesOnOffline = OFFLINE_RETRY_ATTEMPTS_MAX,
                    retriesOnTimeout = OFFLINE_RETRY_ATTEMPTS_MAX,
                    maxRetryWaitSeconds = RETRY_DELAY_MAX,
                } = options || {};

                if (name === 'OfflineError') {
                    if (attempts > retriesOnOffline) {
                        throw e;
                    }
                    return wait(OFFLINE_RETRY_DELAY).then(() => perform(attempts + 1, retriesOnOffline));
                }

                if (name === 'TimeoutError') {
                    if (attempts > retriesOnTimeout) {
                        throw e;
                    }
                    return perform(attempts + 1, retriesOnTimeout);
                }

                const ignoreUnauthorized =
                    Array.isArray(ignoreHandler) && ignoreHandler.includes(HTTP_ERROR_CODES.UNAUTHORIZED);
                const requestUID = getUIDHeaderValue(headers) ?? UID;
                // Sending a request with a UID but without an authorization header is when the public app makes
                // authenticated requests (mostly for persisted sessions), and ignoring "login" or "signup" requests.
                if (
                    status === HTTP_ERROR_CODES.UNAUTHORIZED &&
                    !ignoreUnauthorized &&
                    (UID || (requestUID && !headers?.Authorization))
                ) {
                    return refreshHandler(requestUID).then(
                        () => perform(attempts + 1, RETRY_ATTEMPTS_MAX),
                        (error) => {
                            if (getIsRefreshFailure(error)) {
                                // Disable any further requests on this session if it was created with a UID and the request was done with the failing UID
                                if (UID && requestUID === UID) {
                                    loggedOut = true;
                                    // Inactive session error is only thrown when this error was caused by a logged in session requesting through the same UID
                                    // to have a specific error consumers can use
                                    throw InactiveSessionError(error);
                                }
                                // The original 401 error is thrown to make it more clear that this auth & refresh failure
                                // was caused by an original auth failure and consumers can just check for 401 instead of 4xx
                                throw e;
                            }
                            // Otherwise, this is not actually an authentication error, it might have failed because the API responds with 5xx, or because the client is offline etc
                            // and as such the error from the refresh call is thrown
                            throw error;
                        }
                    );
                }

                const ignoreUnlock = Array.isArray(ignoreHandler) && ignoreHandler.includes(HTTP_ERROR_CODES.UNLOCK);
                if (status === HTTP_ERROR_CODES.UNLOCK && !ignoreUnlock) {
                    const { Details: { MissingScopes: missingScopes = [] } = {} } = e.data || {};
                    return onMissingScopes({
                        scopes: missingScopes,
                        error: e,
                        options,
                    });
                }

                const ignoreTooManyRequests =
                    Array.isArray(ignoreHandler) && ignoreHandler.includes(HTTP_ERROR_CODES.TOO_MANY_REQUESTS);
                if (status === HTTP_ERROR_CODES.TOO_MANY_REQUESTS && !ignoreTooManyRequests) {
                    return retryHandler(e, maxRetryWaitSeconds).then(() => perform(attempts + 1, RETRY_ATTEMPTS_MAX));
                }

                const { code } = getApiError(e);

                if (code === API_CUSTOM_ERROR_CODES.APP_VERSION_BAD) {
                    appVersionBad = true;
                    throw AppVersionBadError();
                }

                const ignoreHumanVerification =
                    Array.isArray(ignoreHandler) &&
                    ignoreHandler.includes(API_CUSTOM_ERROR_CODES.HUMAN_VERIFICATION_REQUIRED);
                if (code === API_CUSTOM_ERROR_CODES.HUMAN_VERIFICATION_REQUIRED && !ignoreHumanVerification) {
                    const { token, methods, title } = getHumanVerificationData(e);

                    const onVerify = (verificationToken: string, tokenType: HumanVerificationMethodType) => {
                        return call(withVerification(options, verificationToken, tokenType));
                    };

                    return onVerification({ token, methods, onVerify, title, error: e });
                }

                const ignoreUserRestrictedState =
                    Array.isArray(ignoreHandler) &&
                    ignoreHandler.includes(API_CUSTOM_ERROR_CODES.USER_RESTRICTED_STATE);
                if (code === API_CUSTOM_ERROR_CODES.USER_RESTRICTED_STATE && !ignoreUserRestrictedState) {
                    const errorMessage = getApiErrorMessage(e);
                    return onUserRestricted({
                        error: e,
                        message: errorMessage || '',
                    });
                }

                throw e;
            });
        };

        return perform(1);
    };

    Object.defineProperties(cb, {
        UID: {
            set(value) {
                UID = value;
            },
        },
    });

    return cb;
}
